import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * Replaces only the cart-items component, keeping the outer drawer open and stable.
 * Section hydration can preserve stale totals and line markup after quantity or plan changes.
 * @param {HTMLElement} currentComponent
 * @param {string} sectionHtml
 * @returns {boolean}
 */
function replaceCartItemsFromSection(currentComponent, sectionHtml) {
  if (!sectionHtml) return false;
  const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
  const sectionId = currentComponent.dataset.sectionId || '';
  const nextComponent = parsed.querySelector('cart-items-component[data-section-id="' + sectionId + '"]');
  if (!(nextComponent instanceof HTMLElement)) return false;

  const drawerInner = currentComponent.closest('.cart-drawer__inner');
  currentComponent.replaceWith(nextComponent);
  for (const scroller of drawerInner?.querySelectorAll('.cart-drawer__content, .cart-drawer__items') ?? []) {
    if (scroller instanceof HTMLElement) scroller.scrollTop = 0;
  }
  return true;
}

/**
 * Fetches the cart section after Shopify has committed the mutation, then swaps only
 * the inner cart component so the drawer remains open.
 * @param {HTMLElement} currentComponent
 * @param {number} [delay]
 * @returns {Promise<boolean>}
 */
async function refreshCartItemsFromServer(currentComponent, delay = 180) {
  const sectionId = currentComponent.dataset.sectionId || '';
  if (!sectionId) return false;
  if (delay > 0) await new Promise((resolve) => setTimeout(resolve, delay));

  const search = new URLSearchParams(window.location.search);
  search.set('sections', sectionId);
  search.set('_thrv_cart_refresh', String(Date.now()));
  const response = await fetch(window.location.pathname + '?' + search.toString(), {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });
  if (!response.ok) return false;
  const sections = await response.json();
  return replaceCartItemsFromSection(currentComponent, sections?.[sectionId]);
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        const drawerInner = this.closest('.cart-drawer__inner');
        drawerInner?.classList.add('cart-drawer--empty');

        const countPill = drawerInner?.querySelector('.cart-drawer__count-pill');
        if (countPill) countPill.textContent = '0';

        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Switches the protected subscription-gift bottle between black and white.
   * The replacement keeps the private gift property so the automatic discount
   * and gift synchronizer can continue to identify it.
   * @param {Event} event
   */
  async onGiftVariantChange(event) {
    const select = event.target instanceof Element ? event.target.closest('.cart-item-card__gift-colour') : null;
    if (!(select instanceof HTMLSelectElement)) return;

    const itemKey = select.dataset.itemKey;
    const currentVariantId = select.dataset.currentVariantId;
    const replacementVariantId = select.value;
    const allowedVariantIds = new Set(['55122513297780', '55122513330548']);

    if (
      !itemKey ||
      !currentVariantId ||
      replacementVariantId === currentVariantId ||
      !allowedVariantIds.has(replacementVariantId)
    ) {
      return;
    }

    const sectionsToUpdate = new Set([this.sectionId]);
    for (const component of document.querySelectorAll('cart-items-component')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionsToUpdate.add(component.dataset.sectionId);
      }
    }

    const sections = Array.from(sectionsToUpdate).join(',');
    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [{ id: itemKey, quantity: 1 }],
        promise: deferredUpdatePromise.promise,
      })
    );

    try {
      this.#disableCartItems();
      this.classList.add('is-updating');
      select.disabled = true;

      const addBody = new FormData();
      addBody.append('id', replacementVariantId);
      addBody.append('quantity', '1');
      addBody.append('properties[_2thrv_subscription_gift]', 'true');
      addBody.append('sections', sections);
      addBody.append('sections_url', window.location.pathname);

      const addResponse = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        body: addBody,
        headers: { Accept: 'application/json' },
      });
      const addedItem = await addResponse.json();
      if (!addResponse.ok || addedItem.errors || addedItem.status) {
        throw new Error(addedItem.description || addedItem.message || 'Unable to change the gift colour');
      }

      const removeResponse = await fetch(
        Theme.routes.cart_update_url,
        fetchConfig('json', {
          body: JSON.stringify({
            updates: { [itemKey]: 0 },
            sections,
            sections_url: window.location.pathname,
          }),
        })
      );
      const updatedCart = await removeResponse.json();
      if (!removeResponse.ok || updatedCart.errors) {
        throw new Error(updatedCart.errors || 'Unable to replace the previous gift colour');
      }

      deferredUpdatePromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updatedCart),
        detail: {
          sections: updatedCart.sections,
          items: updatedCart.items,
          itemCount: updatedCart.item_count,
          source: 'cart-items-component',
          didError: false,
        },
      });

      const sectionHtml = updatedCart.sections?.[this.sectionId];
      if (sectionHtml) {
        await morphSection(this.sectionId, sectionHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        this.#updateCartQuantitySelectorButtonStates();
      } else {
        await sectionRenderer.renderSection(this.sectionId, {
          cache: false,
          mode: this.isDrawer ? 'hydration' : 'full',
        });
      }
      this.#resetDrawerScroll();
    } catch (error) {
      select.value = currentVariantId;
      deferredUpdatePromise.reject(error);
      console.error('[cart-items] Gift colour change failed', error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Unable to change the gift colour',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      resetShimmer(this);
      this.#enableCartItems();
      this.classList.remove('is-updating');
      select.disabled = false;
    }
  }

  /**
   * Switches a one-time line to the configured subscription plan.
   * @param {Event} event
   */
  async onSubscribeUpsell(event) {
    const button = event.target instanceof Element ? event.target.closest('.cart-item-card__subscribe-upsell') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    await this.#replaceLineWithPlan({
      button,
      itemKey: button.dataset.itemKey,
      variantId: button.dataset.variantId,
      quantity: button.dataset.quantity,
      sellingPlanId: button.dataset.sellingPlanId,
      actionName: 'subscription-upgrade',
    });
  }

  /**
   * Switches an existing subscription line to the quarterly three-box plan.
   * @param {Event} event
   */
  async onUpgradeToQuarterly(event) {
    const button = event.target instanceof Element ? event.target.closest('.cart-upgrade-card__button') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    await this.#replaceLineWithPlan({
      button,
      itemKey: button.dataset.itemKey,
      variantId: button.dataset.upgradeVariantId,
      quantity: button.dataset.upgradeQuantity,
      sellingPlanId: button.dataset.upgradeSellingPlanId,
      actionName: 'quarterly-upgrade',
    });
  }

  /**
   * Adds the replacement selling-plan line, removes the original line, then
   * publishes the Horizon 4 cart event so the drawer, count, and product form stay in sync.
   * @param {object} config
   * @param {HTMLButtonElement} config.button
   * @param {string | undefined} config.itemKey
   * @param {string | undefined} config.variantId
   * @param {string | undefined} config.quantity
   * @param {string | undefined} config.sellingPlanId
   * @param {string} config.actionName
   */
  async #replaceLineWithPlan({ button, itemKey, variantId, quantity, sellingPlanId, actionName }) {
    const parsedQuantity = Number(quantity || 1);
    if (!itemKey || !variantId || !sellingPlanId || !parsedQuantity) {
      console.error(`[cart-items] Missing ${actionName} data`);
      return;
    }

    const sectionsToUpdate = new Set([this.sectionId]);
    for (const component of document.querySelectorAll('cart-items-component')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionsToUpdate.add(component.dataset.sectionId);
      }
    }

    const sections = Array.from(sectionsToUpdate).join(',');
    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const performanceMarker = cartPerformance.createStartingMarker(`${actionName}:user-action`);
    const originalContent = button.innerHTML;

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [{ id: itemKey, quantity: parsedQuantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    try {
      this.#disableCartItems();
      this.classList.add('is-updating');
      button.disabled = true;
      button.classList.add('is-loading');

      const subscriptionBoxesPerUnit = {
        '54093911261556': 1,
        '54093911294324': 2,
        '54093911327092': 3,
      }[String(variantId)] || 0;
      const shouldBundleSubscriptionGift =
        subscriptionBoxesPerUnit * parsedQuantity >= 3 &&
        !document.querySelector('.cart-item-card.is-gift-marked');

      let addBody;
      let addHeaders;
      if (shouldBundleSubscriptionGift) {
        addHeaders = {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        };
        addBody = JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: parsedQuantity,
              selling_plan: Number(sellingPlanId),
            },
            {
              id: 55122513297780,
              quantity: 1,
              properties: { _2thrv_subscription_gift: 'true' },
            },
          ],
          sections,
          sections_url: window.location.pathname,
        });
      } else {
        const formBody = new FormData();
        formBody.append('id', variantId);
        formBody.append('quantity', String(parsedQuantity));
        formBody.append('selling_plan', sellingPlanId);
        formBody.append('sections', sections);
        formBody.append('sections_url', window.location.pathname);
        addBody = formBody;
        addHeaders = { Accept: 'application/json' };
      }

      const addResponse = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        body: addBody,
        headers: addHeaders,
      });
      const addedCart = await addResponse.json();
      if (!addResponse.ok || addedCart.errors || addedCart.status) {
        throw new Error(addedCart.description || addedCart.message || `Failed to complete ${actionName}`);
      }

      const removeResponse = await fetch(
        Theme.routes.cart_update_url,
        fetchConfig('json', {
          body: JSON.stringify({
            updates: { [itemKey]: 0 },
            sections,
            sections_url: window.location.pathname,
          }),
        })
      );
      const updatedCart = await removeResponse.json();
      if (!removeResponse.ok || updatedCart.errors) {
        throw new Error(updatedCart.errors || `Failed to remove the previous ${actionName} line`);
      }

      resetShimmer(this);
      this.#updateQuantitySelectors(updatedCart);

      if (!(await refreshCartItemsFromServer(this))) {
        await sectionRenderer.renderSection(this.sectionId, {
          cache: false,
          mode: this.isDrawer ? 'hydration' : 'full',
        });
        this.#updateCartQuantitySelectorButtonStates();
        this.#resetDrawerScroll();
      }

      deferredUpdatePromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updatedCart),
        detail: {
          sections: updatedCart.sections,
          items: updatedCart.items,
          itemCount: updatedCart.item_count,
          source: 'cart-items-component',
          didError: false,
        },
      });
    } catch (error) {
      console.error(`[cart-items] ${actionName} failed`, error);
      deferredUpdatePromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : `Failed to complete ${actionName}`,
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      resetShimmer(this);
      this.#enableCartItems();
      this.classList.remove('is-updating');
      button.disabled = false;
      button.classList.remove('is-loading');
      button.innerHTML = originalContent;
      cartPerformance.measureFromMarker(performanceMarker);
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const lineId = this.refs.cartItemRows[line - 1]?.dataset.key ?? '';
    const body = JSON.stringify({
      id: lineId,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        if (!(await refreshCartItemsFromServer(this))) {
          await sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
          this.#updateCartQuantitySelectorButtonStates();
          this.#resetDrawerScroll();
        }

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });
      })
      .catch((error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async () => {
        if (!(await refreshCartItemsFromServer(this))) {
          await sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** Keeps the refreshed drawer anchored at the first cart line. */
  #resetDrawerScroll() {
    if (!this.isDrawer) return;
    requestAnimationFrame(() => {
      const drawerInner = this.closest('.cart-drawer__inner');
      for (const scroller of drawerInner?.querySelectorAll('.cart-drawer__content, .cart-drawer__items') ?? []) {
        if (scroller instanceof HTMLElement) scroller.scrollTop = 0;
      }
    });
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
